import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"
import fcose from "cytoscape-fcose"
import dagre from "cytoscape-dagre"

cytoscape.use(fcose)
cytoscape.use(dagre)

// Fallback for a node type the server sent no color for.
const FALLBACK_NODE_COLOR = "#9ca3af"

// Max gap between two taps on the same member node to count as a double click.
const DOUBLE_TAP_MS = 300

// Generous spacing so node labels have room and don't overlap horizontally.
const LAYOUTS = {
  force: {
    name: "fcose",
    animate: true,
    randomize: true,
    quality: "default",
    nodeSeparation: 140,
    idealEdgeLength: 110,
  },
  hierarchy: { name: "dagre", rankDir: "TB", nodeSep: 110, rankSep: 120, animate: true },
  social: {
    name: "concentric",
    concentric: (n) => n.degree(),
    levelWidth: () => 2,
    minNodeSpacing: 70,
    animate: true,
  },
}

// Gruppen-Graph: renders Group -> Tenants -> Roles -> Members with Cytoscape.js.
// Layout toggle (force / hierarchy / social), zoom controls and client-side
// filtering. A member shared across tenants is one node bridging both clusters.
export default class extends Controller {
  static targets = [
    "canvas",
    "layoutButton",
    "tenantFilter",
    "roleFilter",
    "statusFilter",
    "employeeToggle",
    "search",
  ]
  // typeColors is rendered from Groups::GraphBuilder::TYPE_COLORS, the single source
  // shared with the legend — see show.html.erb.
  static values = { url: String, memberUrl: String, exportUrl: String, typeColors: Object }

  // The controls are live before the JSON arrives: every action returns early until this.cy
  // exists. A disconnect during the fetch aborts it — Turbo's snapshot preview connects this
  // controller once and the real render a second time, and cytoscape() on the preview's then
  // detached canvas measures NaN and throws in its initial grid layout.
  async connect() {
    this.currentLayout = "force"
    this.loading = new AbortController()
    const elements = await this.loadElements(this.loading.signal)
    if (this.loading.signal.aborted || !elements) return

    this.cy = cytoscape({
      container: this.canvasTarget,
      elements,
      style: this.stylesheet(),
      minZoom: 0.1,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    })

    this.indexMembers()
    this.populateFilters()
    this.wireSelection()
    // Account-less employees are opt-in — hidden until the toggle is switched on.
    this.hideEmployees()
    this.runLayout()
    this.markActiveLayout()
  }

  disconnect() {
    this.loading?.abort()
    this.cy?.destroy()
    this.cy = null
  }

  async loadElements(signal) {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal,
      })
      if (!response.ok) return null
      const { nodes, edges } = await response.json()
      return [...nodes, ...edges]
    } catch {
      return null
    }
  }

  setLayout(event) {
    this.currentLayout = event.params.layout
    this.markActiveLayout()
    if (this.cy) this.runLayout()
  }

  runLayout() {
    const eles = this.cy.elements(":visible")
    eles.layout({ ...LAYOUTS[this.currentLayout], fit: true, padding: 40 }).run()
  }

  markActiveLayout() {
    this.layoutButtonTargets.forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        button.dataset.groupGraphLayoutParam === this.currentLayout,
      )
    })
  }

  zoomIn() {
    if (!this.cy) return
    this.cy.zoom({ level: this.cy.zoom() * 1.3, renderedPosition: this.center() })
  }

  zoomOut() {
    if (!this.cy) return
    this.cy.zoom({ level: this.cy.zoom() / 1.3, renderedPosition: this.center() })
  }

  fit() {
    if (!this.cy) return
    this.cy.fit(this.cy.elements(":visible"), 40)
  }

  center() {
    const box = this.canvasTarget.getBoundingClientRect()
    return { x: box.width / 2, y: box.height / 2 }
  }

  // Precompute, per member node, the tenants / role labels / statuses it connects
  // to, so filtering is a set lookup rather than a graph walk each keystroke.
  indexMembers() {
    this.memberMeta = {}
    this.tenantLabels = {}
    this.cy.nodes('[type = "tenant"]').forEach((t) => (this.tenantLabels[t.id()] = t.data("label")))

    this.cy.nodes('[type = "member"]').forEach((member) => {
      const tenants = new Set()
      const roles = new Set()
      const statuses = new Set()
      member.incomers("edge").forEach((edge) => {
        statuses.add(edge.data("status"))
        const source = edge.source()
        if (source.data("type") === "role") {
          roles.add(source.data("label"))
          source.incomers('node[type = "tenant"]').forEach((t) => tenants.add(t.id()))
        } else if (source.data("type") === "tenant") {
          tenants.add(source.id())
        }
      })
      this.memberMeta[member.id()] = { tenants, roles, statuses }
    })
  }

  populateFilters() {
    this.cy
      .nodes('[type = "tenant"]')
      .map((t) => ({ value: t.id(), label: t.data("label") }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((t) => this.tenantFilterTarget.append(new Option(t.label, t.value)))

    const roleLabels = [...new Set(this.cy.nodes('[type = "role"]').map((r) => r.data("label")))]
    roleLabels
      .sort((a, b) => a.localeCompare(b))
      .forEach((label) => this.roleFilterTarget.append(new Option(label, label)))
  }

  applyFilters() {
    if (!this.cy) return
    const tenant = this.tenantFilterTarget.value
    const role = this.roleFilterTarget.value
    const status = this.statusFilterTarget.value
    const query = this.searchTarget.value.trim().toLowerCase()
    const showEmployees = this.hasEmployeeToggleTarget && this.employeeToggleTarget.checked

    if (!tenant && !role && !status && !query) {
      this.cy.elements().removeClass("hidden")
      if (!showEmployees) this.hideEmployees()
      this.runLayout()
      return
    }

    const visible = this.cy.collection().union(this.cy.nodes('[type = "group"]'))
    this.cy.nodes('[type = "member"]').forEach((member) => {
      if (this.memberMatches(member, tenant, role, status, query)) {
        this.addMemberChain(visible, member, status)
      }
    })
    // Account-less employees carry no role/status, so only surface them when the
    // toggle is on and neither of those filters is active.
    if (showEmployees && !role && !status) {
      this.cy.nodes('[type = "employee"]').forEach((employee) => {
        if (this.employeeMatches(employee, tenant, query)) this.addEmployeeChain(visible, employee)
      })
    }
    visible.filter('node[type = "tenant"]').forEach((t) => visible.merge(t.incomers()))

    this.cy.elements().not(visible).addClass("hidden")
    visible.removeClass("hidden")
    this.runLayout()
  }

  hideEmployees() {
    const employees = this.cy.nodes('[type = "employee"]')
    employees.connectedEdges().addClass("hidden")
    employees.addClass("hidden")
  }

  employeeMatches(employee, tenant, query) {
    const tenantNodeId = `tenant-${employee.data("tenant_id")}`
    if (tenant && tenant !== tenantNodeId) return false
    if (query) {
      const tenantLabel = (this.tenantLabels[tenantNodeId] || "").toLowerCase()
      if (!employee.data("label").toLowerCase().includes(query) && !tenantLabel.includes(query))
        return false
    }
    return true
  }

  // The employee plus its Mandant node and the connecting edge.
  addEmployeeChain(visible, employee) {
    visible.merge(employee)
    visible.merge(employee.incomers())
  }

  // Export the current view as an audit PDF. Passes the active filters plus the
  // account ids of the members visible right now, so the PDF matches the screen.
  // With no filters active, sends nothing — the server exports the whole group.
  exportPdf() {
    if (!this.cy || !this.hasExportUrlValue) return

    const tenant = this.tenantFilterTarget.value
    const role = this.roleFilterTarget.value
    const status = this.statusFilterTarget.value
    const query = this.searchTarget.value.trim()
    const params = new URLSearchParams()

    if (tenant || role || status || query) {
      this.cy.nodes('[type = "member"]:visible').forEach((member) => {
        params.append("account_ids[]", member.data("account_id"))
      })
      if (tenant) params.set("tenant", tenant.replace("tenant-", ""))
      if (role) params.set("role", role)
      if (status) params.set("status", status)
      if (query) params.set("q", query)
    }

    const queryString = params.toString()
    window.open(
      queryString ? `${this.exportUrlValue}?${queryString}` : this.exportUrlValue,
      "_blank",
    )
  }

  memberMatches(member, tenant, role, status, query) {
    const meta = this.memberMeta[member.id()]
    if (tenant && !meta.tenants.has(tenant)) return false
    if (role && !meta.roles.has(role)) return false
    if (status && !meta.statuses.has(status)) return false
    if (query && !this.matchesQuery(member, meta, query)) return false
    return true
  }

  matchesQuery(member, meta, query) {
    if (member.data("label").toLowerCase().includes(query)) return true
    if ([...meta.roles].some((label) => label.toLowerCase().includes(query))) return true
    return [...meta.tenants].some((id) => this.tenantLabels[id].toLowerCase().includes(query))
  }

  // Add the member and, respecting the status filter, its membership edges and the
  // role/tenant/group chain above them.
  addMemberChain(visible, member, status) {
    visible.merge(member)
    member.incomers("edge").forEach((edge) => {
      if (status && edge.data("status") !== status) return
      visible.merge(edge)
      const source = edge.source()
      visible.merge(source)
      visible.merge(source.incomers())
    })
  }

  // Every node type is selectable. A single click highlights the related
  // elements (a member's roles/tenants upward, a role's members, a tenant's whole
  // subtree). A double click on a member also opens its detail drawer — Cytoscape
  // core has no native double-tap event, so it's detected here by timing.
  wireSelection() {
    this.cy.on("tap", 'node[type = "member"]', (event) => {
      const member = event.target
      const now = Date.now()
      const isDoubleTap =
        this.lastTap?.id === member.id() && now - this.lastTap.time < DOUBLE_TAP_MS
      this.lastTap = { id: member.id(), time: now }

      this.highlightMember(member)
      if (isDoubleTap) this.openMemberDetails(member.data("account_id"))
    })
    this.cy.on("tap", 'node[type = "role"]', (event) => this.highlightRole(event.target))
    this.cy.on("tap", 'node[type = "tenant"]', (event) => this.highlightTenant(event.target))
    this.cy.on("tap", (event) => {
      if (event.target === this.cy) {
        this.lastTap = null
        this.cy.elements().removeClass("faded highlighted")
      }
    })
  }

  // Two-level highlight upward: the member, its role nodes AND the tenants above
  // those roles (plus every connecting edge).
  highlightMember(member) {
    const chain = member.closedNeighborhood()
    chain.nodes('[type = "role"]').forEach((role) => chain.merge(role.incomers()))
    this.applyHighlight(chain)
  }

  // A role plus everyone who holds it (and the tenant it belongs to, for context).
  highlightRole(role) {
    this.applyHighlight(role.closedNeighborhood().union(role.successors()))
  }

  // A tenant plus its full subtree: every role and member below it.
  highlightTenant(tenant) {
    this.applyHighlight(tenant.closedNeighborhood().union(tenant.successors()))
  }

  applyHighlight(collection) {
    this.cy.elements().removeClass("faded highlighted")
    this.cy.elements().not(collection).addClass("faded")
    collection.addClass("highlighted")
  }

  // Fetch and render the member side drawer (a turbo-stream that appends the
  // offcanvas panel). Silent no-op if the endpoint is unavailable.
  openMemberDetails(accountId) {
    if (!accountId || !this.hasMemberUrlValue) return
    const url = this.memberUrlValue.replace("__ID__", accountId)
    fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => html && window.Turbo.renderStreamMessage(html))
      .catch(() => {})
  }

  stylesheet() {
    return [
      {
        selector: "node",
        style: {
          "background-color": (n) => this.typeColorsValue[n.data("type")] || FALLBACK_NODE_COLOR,
          label: "data(label)",
          color: "#374151",
          "font-size": 11,
          "text-valign": "bottom",
          "text-margin-y": 4,
          "min-zoomed-font-size": 7,
          // Wrap long names and give each label a white halo so neighbouring
          // labels stay readable instead of overlapping into an unreadable blur.
          "text-wrap": "wrap",
          "text-max-width": 120,
          "text-background-color": "#ffffff",
          "text-background-opacity": 0.85,
          "text-background-shape": "roundrectangle",
          "text-background-padding": 2,
          width: (n) => this.nodeSize(n),
          height: (n) => this.nodeSize(n),
        },
      },
      {
        selector: 'node[type = "group"]',
        style: { color: "#111827", "font-size": 15, "font-weight": "bold", "text-max-width": 180 },
      },
      {
        selector: 'node[type = "tenant"]',
        style: { color: "#065f46", "font-size": 13, "font-weight": "bold", "text-max-width": 150 },
      },
      {
        selector: "node[?archived]",
        style: { "background-opacity": 0.4, "border-width": 1, "border-color": "#9ca3af" },
      },
      {
        selector: "edge",
        style: {
          width: 1.5,
          "line-color": "#cbd5e1",
          "curve-style": "bezier",
          "target-arrow-shape": "none",
          opacity: 0.8,
        },
      },
      {
        selector: 'edge[status = "suspended"]',
        style: { "line-style": "dashed", "line-color": "#d1d5db" },
      },
      { selector: 'edge[status = "invited"]', style: { "line-style": "dotted" } },
      { selector: "edge[?has_overrides]", style: { "line-color": "#f59e0b", width: 2 } },
      { selector: ".hidden", style: { display: "none" } },
      { selector: ".faded", style: { opacity: 0.15 } },
      {
        selector: "node.highlighted",
        style: { "border-width": 3, "border-color": "#149d71" },
      },
      {
        selector: "edge.highlighted",
        style: { "line-color": "#149d71", width: 2.5, opacity: 1 },
      },
    ]
  }

  // Members scale with how many tenants they span (cross-company operators pop);
  // group and tenant nodes are fixed larger anchors.
  nodeSize(node) {
    switch (node.data("type")) {
      case "group":
        return 46
      case "tenant":
        return 34
      case "role":
        return 20
      default:
        return 18 + Math.min((node.data("tenant_count") || 1) - 1, 4) * 6
    }
  }
}
